import * as React from 'react'

import { ApolloClient } from '@apollo/client'
import {
  ShiftManagementPageDocument,
  ShiftManagementSavedShiftsCrumbDocument,
  ShiftManagementTemplateWeeksCrumbDocument,
  ShiftManagementTemplatesCrumbDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { settingsOptions } from './settings'

const RootPage = React.lazy(() => import('modules/shift_management/pages/RootPage'))
const MainPage = React.lazy(() => import('modules/shift_management/pages/MainPage'))
const EditSavedShift = React.lazy(() => import('modules/shift_management/pages/EditSavedShiftPage'))
const EditShiftTemplatePage = React.lazy(
  () => import('modules/shift_management/pages/EditShiftTemplatePage')
)
const EditTemplateWeekPage = React.lazy(
  () => import('modules/shift_management/pages/EditTemplateWeekPage')
)
const NavigateToEditShiftTemplatePage = React.lazy(
  () => import('modules/shift_management/pages/NavigateToEditShiftTemplatePage')
)
const NavigateToViewIndexPage = React.lazy(
  () => import('modules/shift_management/pages/NavigateToViewIndexPage')
)
const NewSavedShift = React.lazy(() => import('modules/shift_management/pages/NewSavedShiftPage'))
const SavedShiftsPage = React.lazy(() => import('modules/shift_management/pages/SavedShiftsPage'))
const ShiftTemplatesPage = React.lazy(
  () => import('modules/shift_management/pages/ShiftTemplatesPage')
)
const ExportAnnualBalancePage = React.lazy(
  () => import('modules/shift_management/pages/ExportAnnualBalancePage')
)
const ExportSchedulePage = React.lazy(
  () => import('modules/shift_management/pages/ExportSchedulePage')
)

const PrintSchedulePage = React.lazy(
  () => import('modules/shift_management/pages/PrintSchedulePage')
)

const InsightsRequestPage = React.lazy(
  () => import('modules/shift_management/pages/InsightsRequestPage')
)
const InsightsSidepanelPage = React.lazy(
  () => import('modules/shift_management/pages/InsightsSidepanelPage')
)

const settingsMetadata = ({
  watchQuery,
  navigation,
  title,
  description,
}: {
  watchQuery: ApolloClient<unknown>['watchQuery']
  navigation: Navigation<NavigationPaths>
  title: string
  description: string
}) => {
  return watchQuery({
    query: ShiftManagementPageDocument,
  }).map(({ data }) => {
    const canManageDefaultShifts = data?.shiftManagement?._policies.defaultShiftManage.can
    const canManageTemplates = data?.shiftManagement?._policies.accessManageTemplates.can

    return {
      title: title,
      description: description,
      subNavigation: [
        canManageDefaultShifts
          ? {
              title: i18n.t('shift_management.saved_shifts.title'),
              href: navigation.shifts.savedShifts.fullPath(),
            }
          : null,
        canManageTemplates
          ? {
              title: i18n.t('shift_management.rotating_shifts.section_title'),
              href: navigation.shifts.templates.fullPath(),
            }
          : null,
      ],
    }
  })
}

export const shiftsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.shifts.path,
      element: <RootPage />,
      handle: {
        module: 'shift_management',
        moduleName: () => i18n.t('shift_management.section.title'),
        meta: ({ params, location, watchQuery }) => {
          return settingsOptions(params, location, navigation, watchQuery)
        },
      },
      children: [
        {
          index: true,
          element: <NavigateToViewIndexPage />,
        },
        {
          // keep supporting old route
          path: navigation.shifts.customView.path,
          element: <NavigateToViewIndexPage />,
        },
        {
          path: navigation.shifts.customViewGroup.path,
          element: <MainPage />,
          children: [
            {
              path: navigation.shifts.customViewGroup.exportAnnualBalance.path,
              element: <ExportAnnualBalancePage />,
            },
            {
              path: navigation.shifts.customViewGroup.exportSchedule.path,
              element: <ExportSchedulePage />,
            },
            {
              path: navigation.shifts.customViewGroup.printSchedule.path,
              element: <PrintSchedulePage />,
            },
            {
              path: navigation.shifts.customViewGroup.insightsRequest.path,
              element: <InsightsRequestPage />,
            },
            {
              path: navigation.shifts.customViewGroup.insightsSidepanel.path,
              element: <InsightsSidepanelPage />,
            },
          ],
        },

        {
          path: navigation.shifts.savedShifts.path,
          handle: {
            meta: ({ watchQuery }) =>
              settingsMetadata({
                watchQuery,
                navigation,
                title: i18n.t('shift_management.saved_shifts.title'),
                description: i18n.t('shift_management.saved_shifts.description_2'),
              }),
          },
          children: [
            {
              index: true,
              element: <SavedShiftsPage />,
            },
            {
              path: navigation.shifts.savedShifts.newSavedShift.path,
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('shift_management.saved_shifts.form.title'),
                  description: i18n.t('shift_management.saved_shifts.form.description'),
                }),
              },
              element: <NewSavedShift />,
            },
            {
              path: navigation.shifts.savedShifts.editSavedShift.path,
              handle: {
                resource: true,
                meta: ({ params, watchQuery }) => {
                  const id = params['id']
                  if (!id) return { title: '' }

                  return watchQuery({
                    query: ShiftManagementSavedShiftsCrumbDocument,
                    fetchPolicy: 'cache-first',
                    variables: {
                      ids: [String(id)],
                    },
                  }).map((result) => {
                    const savedShift =
                      result?.data?.shiftManagement?.defaultShiftsConnection?.edges?.[0]?.node
                    if (!savedShift) return { title: '' }

                    return {
                      title: savedShift.title,
                    }
                  })
                },
              },
              element: <EditSavedShift />,
            },
          ],
        },

        {
          path: navigation.shifts.templates.path,
          handle: {
            meta: ({ watchQuery }) =>
              settingsMetadata({
                watchQuery,
                navigation,
                title: i18n.t('shift_management.rotating_shifts.section_title'),
                description: i18n.t('shift_management.rotating_shifts.settings_description'),
              }),
          },
          children: [
            {
              index: true,
              element: <ShiftTemplatesPage />,
            },
            {
              path: navigation.shifts.templates.templatesShiftTemplateId.path,
              children: [
                {
                  index: true,
                  element: <NavigateToEditShiftTemplatePage />,
                },
                {
                  path: navigation.shifts.templates.templatesShiftTemplateId.edit.path,
                  handle: {
                    resource: true,
                    meta: ({ params, watchQuery }) => {
                      const id = params['shiftTemplateId']
                      if (!id) return { title: '' }

                      return watchQuery({
                        query: ShiftManagementTemplatesCrumbDocument,
                        fetchPolicy: 'cache-first',
                        variables: {
                          ids: [String(id)],
                        },
                      }).map((result) => {
                        const template =
                          result?.data?.shiftManagement?.templatesConnection?.edges?.[0]?.node
                        if (!template) return { title: '' }

                        return {
                          title: template.name,
                        }
                      })
                    },
                  },
                  children: [
                    {
                      index: true,
                      element: <EditShiftTemplatePage />,
                    },
                    {
                      path: navigation.shifts.templates.templatesShiftTemplateId.edit
                        .weeksWeekIdEdit.path,
                      element: <EditTemplateWeekPage />,
                      handle: {
                        resource: true,
                        meta: ({ params, watchQuery }) => {
                          const templateId = params['shiftTemplateId']
                          if (!templateId) return { title: '' }

                          return watchQuery({
                            query: ShiftManagementTemplateWeeksCrumbDocument,
                            fetchPolicy: 'cache-first',
                            variables: {
                              templateId: String(templateId),
                            },
                          }).map((result) => {
                            const templateWeeks =
                              result?.data?.shiftManagement?.templateWeeksConnection?.edges.map(
                                (e) => e.node
                              ) || []
                            if (!templateWeeks.length) return { title: '' }

                            const templateWeekId = String(params['weekId'])
                            const templateWeek = templateWeeks.find(
                              (tw) => tw.id === templateWeekId
                            )
                            return {
                              title: templateWeek?.name || '',
                            }
                          })
                        },
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}
